import { Fragment, useState } from "react";

import { isPastDate } from "@bokio/bank/src/utils/paymentUtils";
import { getWarningsFromPaymentGroup, PaymentWarnings } from "@bokio/components/PaymentWarnings/PaymentWarnings";
import { Button } from "@bokio/designsystem/components/Button";
import { CheckBox } from "@bokio/designsystem/components/CheckBox/CheckBox";
import Icon from "@bokio/designsystem/components/Icon/Icon";
import { Notice } from "@bokio/designsystem/components/Notice/Notice";
import { Td, Tr } from "@bokio/elements/Table";
import { BankLangFactory, GeneralLangFactory } from "@bokio/lang";
import { useCompanyInfo } from "@bokio/mobile-web-shared/core/contexts/CompanyInfoContext/CompanyInfoContext";
import { getDisplayedPaymentFor } from "@bokio/shared/utils/Bank/renderTellerPaymentInformationUnion";
import { formatDate, formatNumberCurrency } from "@bokio/shared/utils/format";
import { mergeClassNames } from "@bokio/utils/classes";

import type * as m from "@bokio/mobile-web-shared/core/model/model";
import type { FC } from "react";

import * as contentStyles from "../SignAndPayModal/SignAndPayContent/signAndPayContentGrid.scss";
import * as styles from "./signAndPayRow.scss";

type PaymentValidationRulesDto = m.Bokio.Bank.Contract.Dtos.PaymentValidationRulesDto;
type PaymentGroup = m.Bokio.Bank.Contract.Dtos.PaymentGroupForListDto;
type FailedPaymentInfo = m.Bokio.Bank.Contract.Dtos.FailedPaymentInfo;
interface ErrorNoticeProps {
	testId?: string;
	errorMessage?: string;
}

const ErrorNotice: FC<ErrorNoticeProps> = props => (
	<Notice margin={[]} color="error" testId={props.testId}>
		{props.errorMessage || GeneralLangFactory().SomethingWentWrongWeAreSorry}
	</Notice>
);

interface SignAndPayRowProps {
	paymentGroup: PaymentGroup;
	toggle: (id: string, checked: boolean) => void;
	disabled: boolean;
	selected?: boolean;
	isPermitted?: boolean;
	failedPayments: Record<string, FailedPaymentInfo>;
	paymentValidationRules: PaymentValidationRulesDto;
}

export const SignAndPayRow: FC<SignAndPayRowProps> = ({
	paymentGroup,
	toggle,
	disabled,
	selected,
	isPermitted = true,
	failedPayments,
	paymentValidationRules,
}) => {
	const generalLang = GeneralLangFactory();
	const bankLang = BankLangFactory();
	const { companyInfo } = useCompanyInfo();
	const domesticCurrency = companyInfo.CountryMetadata.Currency;

	const [open, setOpen] = useState(false);

	const toggleOpen = () => {
		setOpen(!open);
	};

	const payment = paymentGroup.Payments[0];
	const hasErrors = paymentGroup.Payments.some(p => failedPayments[p.Id]);
	const isForeignCurrency = payment.Currency !== domesticCurrency;
	const groupDomesticTotal = paymentGroup.Payments.reduce((sum, p) => sum + p.ApproximateDomesticSum, 0);
	const rowDisabled = disabled || !isPermitted;

	//Row is part of grid so its important the column count match. Each row must consist of multiple of 6 columns to align correctly.
	return (
		<>
			<Tr
				testId="SignAndPay_Payment_Row"
				isActive={open}
				className={mergeClassNames(hasErrors && styles.error, !isPermitted && styles.notPermitted)}
			>
				<td className={contentStyles.itemGroupExpand}>
					{/* {paymentGroup.IsBatch && ( */}
					{paymentGroup.IsBatch && (
						<Icon
							onClick={toggleOpen}
							name={open ? "down-open-big" : "right-open-big"}
							testId="PaymentGroupRow_ToggleOpen"
							className={mergeClassNames(styles.originalColor, styles.toggleOpenIcon)}
						/>
					)}
				</td>
				<td className={contentStyles.itemSelect}>
					<CheckBox
						testId={`SelectPayment_${paymentGroup.Id}`}
						disabled={rowDisabled}
						className={styles.margin0}
						checked={selected}
						onChange={selected => toggle(paymentGroup.Id, selected)}
					/>
				</td>
				<td className={mergeClassNames(contentStyles.itemDescription, !selected && styles.linethrough)}>
					{paymentGroup.IsBatch
						? paymentGroup.GroupReference
						: getDisplayedPaymentFor(payment.SenderReference, payment.PaymentInfoUnion)}
				</td>
				<td className={mergeClassNames(contentStyles.itemDate)}>
					<span className={styles.mobileHeader}>{generalLang.Date}</span>
					<span className={mergeClassNames(!selected && styles.linethrough)}>
						{isPastDate(payment) && selected
							? formatDate(paymentValidationRules.NextPossiblePaymentDateIncludingToday)
							: formatDate(paymentGroup.Date)}
					</span>
				</td>
				<Td testId="SignAndPayRow_PaymentWarnings" className={mergeClassNames(contentStyles.itemWarnings)}>
					<PaymentWarnings
						isGroup={paymentGroup.IsBatch}
						warnings={getWarningsFromPaymentGroup(paymentGroup)}
						isSigning
					/>
				</Td>
				<td className={mergeClassNames(contentStyles.itemSum)}>
					<span className={styles.mobileHeader}>{generalLang.Sum}</span>
					<div className={mergeClassNames(styles.currencyContainer, !selected && styles.linethrough)}>
						{isForeignCurrency ? (
							<>
								<span>{`~ ${formatNumberCurrency(groupDomesticTotal, domesticCurrency)}`}</span>
								<span>{`(${formatNumberCurrency(paymentGroup.Sum, payment.Currency)})`}</span>
							</>
						) : (
							formatNumberCurrency(paymentGroup.Sum, payment.Currency)
						)}
					</div>
				</td>
				<td className={mergeClassNames(contentStyles.itemPayLater, styles.buttons)} align="right">
					<Button
						disabled={rowDisabled}
						appearance="secondary"
						icon={selected ? undefined : "plus"}
						onClick={() => toggle(paymentGroup.Id, !selected)}
					>
						{selected ? bankLang.SignAndPayRow_MoveForLater_Button : bankLang.SignAndPayRow_AddToPay_Button}
					</Button>
				</td>

				{!paymentGroup.IsBatch && failedPayments[payment.Id] && (
					<td className={contentStyles.itemError}>
						<ErrorNotice testId="PaymentGroupRow_ErrorNotice" errorMessage={failedPayments[payment.Id].ErrorMessage} />
					</td>
				)}
			</Tr>
			{paymentGroup.IsBatch &&
				open &&
				paymentGroup.Payments.map(p => (
					<Fragment key={p.Id}>
						<tr className={mergeClassNames(styles.batchRow, failedPayments[p.Id] && styles.error)}>
							<td />
							<td />
							<td className={contentStyles.itemDescription}>
								{getDisplayedPaymentFor(p.SenderReference, p.PaymentInfoUnion)}
							</td>
							<td />
							<td>
								<PaymentWarnings isGroup={false} warnings={p.Warnings} isSigning />
							</td>
							<td className={contentStyles.itemSum} align="right">
								<span className={styles.mobileHeader}>{generalLang.Sum}</span>
								{formatNumberCurrency(p.Sum, p.Currency)}
							</td>
						</tr>
						{failedPayments[p.Id] && (
							<ErrorNotice testId="PaymentRow_ErrorNotice" errorMessage={failedPayments[p.Id].ErrorMessage} />
						)}
					</Fragment>
				))}
		</>
	);
};
